import { useMemo, useState } from 'react'
import { BatteryCharging, PlugZap, Zap } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Link } from '../../../router.js'
import { fmtHeure, Pastille, useVueSonde } from '../commun.js'
import { Bandeaux, donneeDe, EnTeteDomaine, LienAppareil, Lu, Panneau, Sources, usePeutDetails, Vide, Zone } from '../video/kit.js'
import { fmtDecimal, lienIncident } from '../video/kit-modele.js'
import { Courbe } from '../fiches/Courbe.js'
import { fmtValeur } from '../fiches/modele.js'
import { estPowerDoc, finAutonomie, type PowerDoc } from './batiment-modele.js'
import {
  ancreOnduleur, cheminHistorique, deroule, estDocAutonomie, estDocHistoriqueOnduleur, etatDetaille, fmtDureeS, fmtMinutes,
  LIBELLE_BIP, LIBELLE_LIMITE, LIBELLE_PERIODE, libellePrise, onduleursRanges, PERIODES_HISTORIQUE, plagesBatterie,
  postesParOnduleur, seriesTracables, texteAlimentations, TON_LIMITE,
  type AutonomiePoste, type DocAutonomie, type OnduleurDetaille, type PeriodeHistorique,
} from './onduleurs-modele.js'
import './batiment.css'

/**
 * TECHNIQUE › BÂTIMENT › ONDULEURS — /technique/batiment/onduleurs (27/10/2026).
 *
 * Demande d'Hugo : les 3 onduleurs (tensions d'entrée et de sortie, charge,
 * autonomie, prises, bip), l'historique 24 h / 7 j / 30 j avec les passages
 * sur batterie surlignés, l'autonomie de CHAQUE appareil selon son propre
 * onduleur, et « Si le courant coupe » : l'ordre des chutes et ce que chacune
 * emporte. Lu sur Sonde (`power`, `power/runtime`, `power/ups/:uid/history`),
 * droit sonde.read, sans rien modifier : les alimentations A/B d'un appareil
 * se règlent dans sa fiche du registre, le délai d'arrêt du Dell dans
 * Administration › Supervision › Alimentation.
 */

export function OnduleursPage() {
  const meta = useShellMeta()
  const details = usePeutDetails()
  const pw = useVueSonde('power', estPowerDoc)
  const rt = useVueSonde('power/runtime', estDocAutonomie, 30_000)
  const P = donneeDe(pw.lecture, pw.precedente)
  const R = donneeDe(rt.lecture, rt.precedente)
  const ups = useMemo(() => (P.d ? onduleursRanges(P.d.ups as OnduleurDetaille[]) : []), [P.d])
  const postes = useMemo(() => (R.d ? postesParOnduleur(R.d) : null), [R.d])

  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger sv-dom-page sv-bat sv-ond">
        <EnTeteDomaine
          retour={{ href: '/technique/batiment', label: 'Bâtiment' }}
          icone={<BatteryCharging size={26} />}
          titre="Onduleurs"
          sousTitre="Les onduleurs du club, leurs tensions et leur historique, et combien de temps chaque appareil tient si le courant coupe. Lu sur Sonde, sans rien modifier."
          genereA={P.d?.generated_at}
          perime={P.perime}
        />
        <Bandeaux lecture={pw.lecture} genereA={P.d?.generated_at} collector={P.d?.collector} />

        {P.d?.mains_outage.active ? <Coupure d={P.d} /> : null}

        <Panneau id="sv-ond-coupure" titre="Si le courant coupe" indice={R.d ? `le Dell s’arrête proprement au bout de ${fmtMinutes(R.d.shutdown_delay_min)} sur batterie` : undefined}>
          {R.d ? <Zone perime={R.perime}><Deroule d={R.d} /></Zone>
            : <p className="lc-hint">{rt.lecture && rt.lecture.etat !== 'ok' ? rt.lecture.message : 'Lecture en cours…'}</p>}
        </Panneau>

        {P.d ? (
          ups.length ? (
            <Zone perime={P.perime}>
              <div className="sv-ond-cartes">
                {ups.map((u) => <CarteOnduleur key={u.device.uid} u={u} postes={postes?.parOnduleur.get(u.device.uid) ?? null} details={details} />)}
              </div>
            </Zone>
          ) : (
            <Panneau id="sv-ond-aucun" titre="Onduleurs">
              <Vide titre="Aucun onduleur au registre de Sonde pour l’instant." versADecider>
                <p>Sonde affiche ici chaque onduleur dès qu’il est validé dans son registre.</p>
              </Vide>
            </Panneau>
          )
        ) : !pw.lecture ? <p className="lc-hint">Lecture en cours…</p> : null}

        {postes && postes.sans.length ? (
          <Panneau id="sv-ond-sans" titre="Appareils sans onduleur" indice="sur secteur seul, ou alimentation non renseignée">
            <Zone perime={R.perime}><ListePostes postes={postes.sans} /></Zone>
          </Panneau>
        ) : null}

        {P.d ? (
          <Panneau id="sv-ond-sources" titre="Sources" indice="d’où viennent ces chiffres">
            <Sources sources={P.d.sources} details={details} />
          </Panneau>
        ) : null}
      </main>
    </Shell>
  )
}

/* ─────────────────────────────── Coupure en cours */

function Coupure({ d }: { d: PowerDoc }) {
  const surBatterie = d.ups.find((u) => u.on_battery === true) ?? null
  const fin = surBatterie ? finAutonomie(surBatterie, Date.now()) : null
  return (
    <div className="sv-bat-coupure" role="alert">
      <PlugZap size={22} aria-hidden="true" />
      <div className="sv-bat-coupure__corps">
        <p className="sv-bat-coupure__titre">
          Coupure secteur{d.mains_outage.since ? ` depuis ${fmtHeure(d.mains_outage.since)}` : ''}
          {fin ? ` : environ ${fmtDecimal(fin.reste_min, 'min', 0)} d’autonomie sur ${surBatterie!.device.display_name}.` : '.'}
        </p>
        {d.mains_outage.incident_id ? (
          <p className="sv-bat-coupure__meta"><Link className="sv-dom-lien" href={lienIncident(d.mains_outage.incident_id)}>Ouvrir l’incident</Link></p>
        ) : null}
      </div>
    </div>
  )
}

/* ─────────────────────────────── Si le courant coupe */

function Deroule({ d }: { d: DocAutonomie }) {
  const etapes = deroule(d)
  return (
    <>
      {d.summary_fr ? <p className="sv-ond-resume">{d.summary_fr}</p> : null}
      {etapes.length === 0 ? <p className="lc-hint">Sonde ne peut pas encore dérouler une coupure : l’autonomie des onduleurs n’est pas lue.</p> : (
        <ol className="sv-ond-frise">
          {etapes.map((e) => (
            <li key={e.cle} className="sv-ond-etape">
              <span className="sv-ond-etape__quand">{e.quand}</span>
              <div className="sv-ond-etape__corps">
                <p className="sv-ond-etape__titre">{e.titre}</p>
                {/* La phrase de Sonde nomme déjà ce qui s'éteint ; la liste ne vient qu'à défaut. */}
                {e.consequence ? <p className="sv-ond-etape__texte">{e.consequence}</p>
                  : e.tombent.length ? <p className="sv-ond-etape__liste"><strong>S’éteignent :</strong> {e.tombent.join(', ')}</p> : null}
                {e.injoignables.length ? <p className="sv-ond-etape__liste"><strong>Deviennent injoignables :</strong> {e.injoignables.join(', ')}</p> : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

/* ─────────────────────────────── Une carte par onduleur */

function CarteOnduleur({ u, postes, details }: { u: OnduleurDetaille; postes: AutonomiePoste[] | null; details: boolean }) {
  const e = etatDetaille(u)
  const prises = u.outlets ?? []
  const modele = [u.model, details && u.serial ? `n° ${u.serial}` : null].filter(Boolean).join(' · ')
  return (
    <article id={ancreOnduleur(u.device.uid)} className={`lc-panel sv-ond-carte sv-bat-onduleur--${e.ton}`} aria-labelledby={`${ancreOnduleur(u.device.uid)}-titre`}>
      <header className="sv-ond-carte__tete">
        <h2 className="sv-bat-onduleur__nom" id={`${ancreOnduleur(u.device.uid)}-titre`}>
          <BatteryCharging size={18} aria-hidden="true" /> <LienAppareil uid={u.device.uid} nom={u.device.display_name} zone={u.device.zone_code} />
        </h2>
        <Pastille ton={e.ton}>{e.libelle}</Pastille>
      </header>
      {modele ? <p className="sv-ond-carte__modele">{modele}</p> : null}

      <div className="sv-ond-carte__chiffres">
        <p className="sv-bat-onduleur__autonomie">
          <span className="sv-bat-onduleur__chiffre">{fmtMinutes(u.runtime_min)}</span> <span className="sv-bat-onduleur__unite">d’autonomie</span>
        </p>
        <p className="sv-bat-onduleur__autonomie">
          <span className="sv-ond-carte__second">{fmtDecimal(u.charge_pct, '%', 0)}</span> <span className="sv-bat-onduleur__unite">de batterie</span>
        </p>
      </div>

      <dl className="sv-dom-faits sv-ond-faits">
        <div><dt>Charge utile</dt><dd>{fmtDecimal(u.load_pct, '%', 0)}</dd></div>
        <div><dt>Puissance</dt><dd>{fmtDecimal(u.power_w, 'W', 0)}{u.nominal_w ? ` sur ${fmtDecimal(u.nominal_w, 'W', 0)}` : ''}</dd></div>
        <div><dt>Entrée</dt><dd>{fmtDecimal(u.input_v, 'V', 0)}{u.input_freq_hz != null ? ` · ${fmtDecimal(u.input_freq_hz, 'Hz')}` : ''}</dd></div>
        <div><dt>Sortie</dt><dd>{fmtDecimal(u.output_v, 'V', 0)}{u.output_freq_hz != null ? ` · ${fmtDecimal(u.output_freq_hz, 'Hz')}` : ''}</dd></div>
        <div><dt>Batterie</dt><dd>{fmtDecimal(u.battery_v, 'V')}</dd></div>
        {u.transfer_low_v != null || u.transfer_high_v != null ? (
          <div><dt>Bascule sur batterie</dt><dd>sous {fmtDecimal(u.transfer_low_v, 'V', 0)} ou au-dessus de {fmtDecimal(u.transfer_high_v, 'V', 0)}</dd></div>
        ) : null}
        {prises.length ? <div><dt>Prises</dt><dd>{prises.map(libellePrise).join(' · ')}</dd></div> : null}
        {u.beeper ? <div><dt>Bip</dt><dd>{LIBELLE_BIP[u.beeper]}</dd></div> : null}
      </dl>
      <Lu age={u.data_age_s} source="Onduleur (NUT)" />

      <Historique uid={u.device.uid} nom={u.device.display_name} />

      <section className="sv-ond-carte__postes" aria-label={`Appareils de ${u.device.display_name}`}>
        <h3 className="sv-bat-sous-titre">Appareils branchés</h3>
        {postes === null ? <p className="lc-hint">Autonomie par appareil : lecture en cours…</p>
          : postes.length === 0 ? <p className="lc-hint">Aucun appareil rattaché à cet onduleur dans le registre de Sonde.</p>
          : <ListePostes postes={postes} />}
      </section>
    </article>
  )
}

function ListePostes({ postes }: { postes: AutonomiePoste[] }) {
  return (
    <ul className="sv-dom-lignes sv-ond-postes">
      {postes.map((p) => (
        <li key={p.device.uid} className="sv-dom-ligne sv-ond-poste">
          <span className="sv-dom-ligne__nom"><LienAppareil uid={p.device.uid} nom={p.device.display_name} zone={p.device.zone_code} /></span>
          <Pastille ton={TON_LIMITE[p.limited_by] ?? 'gris'}>{p.limited_by === 'mains_only' ? 'Aucune' : fmtMinutes(p.runtime_min)}</Pastille>
          <span className="sv-dom-ligne__meta">
            {p.text_fr || LIBELLE_LIMITE[p.limited_by]}
            {p.feeds.length > 1 ? ` · ${texteAlimentations(p.feeds)}` : ''}
          </span>
        </li>
      ))}
    </ul>
  )
}

/* ─────────────────────────────── Historique d'un onduleur */

function Historique({ uid, nom }: { uid: string; nom: string }) {
  const [periode, setPeriode] = useState<PeriodeHistorique>('24h')
  const { lecture, precedente } = useVueSonde(cheminHistorique(uid, periode), estDocHistoriqueOnduleur, 120_000)
  // Une lecture d'une autre période ne s'affiche pas sous ce bouton.
  const brut = donneeDe(lecture, precedente)
  const d = brut.d && brut.d.range === periode ? brut.d : null
  const debut = d ? Date.parse(d.from) : 0
  const fin = d ? Date.parse(d.to) : 0
  const plages = d ? plagesBatterie(d.battery_periods, fin) : []
  const series = d ? seriesTracables(d) : []

  return (
    <section className={`sv-ond-histo${brut.perime ? ' sv-fiche-perime' : ''}`} aria-label={`Historique de ${nom}`}>
      <div className="sv-ond-histo__tete">
        <h3 className="sv-bat-sous-titre">Historique</h3>
        <SegmentedControl size="sm" ariaLabel={`Période de l’historique de ${nom}`} value={periode} onChange={setPeriode}
          options={PERIODES_HISTORIQUE.map((p) => ({ value: p, label: LIBELLE_PERIODE[p] }))} />
      </div>
      {!d ? <p className="lc-hint">{lecture && lecture.etat !== 'ok' ? lecture.message : 'Lecture en cours…'}</p> : (
        <>
          {series.length === 0 ? <p className="lc-hint">Aucune mesure sur la période — pas un zéro : Sonde n’a rien relevé.</p> : series.map((s) => (
            <figure key={`${s.metric}-${JSON.stringify(s.key)}`} className="sv-fiche-courbe">
              <figcaption className="sv-fiche-courbe__titre">
                <span>{s.label}</span>
                <span className="sv-fiche-courbe__valeur">{fmtValeur(derniere(s.points), s.unit)}</span>
              </figcaption>
              <Courbe points={s.points} seuilS={s.gap_threshold_s} unite={s.unit} agg={s.agg} debut={debut} fin={fin} hauteur={72}
                      libelle={`${nom} — ${s.label}`} marques={plages} marqueLibelle="Sur batterie" />
            </figure>
          ))}
          {d.battery_periods.length ? (
            <ul className="sv-ond-passages" aria-label="Passages sur batterie">
              {d.battery_periods.map((p) => (
                <li key={p.from}><Zap size={14} aria-hidden="true" /> {p.text_fr || `Sur batterie ${fmtDureeS(p.duration_s)}`}</li>
              ))}
            </ul>
          ) : <p className="lc-hint">Aucun passage sur batterie sur la période.</p>}
        </>
      )}
    </section>
  )
}

function derniere(points: [number, number | null, number | null, number | null][]): number | null {
  for (let i = points.length - 1; i >= 0; i--) { const v = points[i]![1]; if (v !== null) return v }
  return null
}
